import { useCallback, useEffect, useMemo, useState } from 'react';
import { translations, type Language, type TranslationKey } from './translations';
import { I18nContext, type I18nContextValue } from './useI18n';
import { editorTranslationsEn } from './editorTranslations';

const LANGUAGE_STORAGE_KEY = 'sloy-language';
type TranslationParams = Record<string, string | number>;

function getInitialLanguage(): Language {
  const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return storedLanguage === 'en' || storedLanguage === 'ru' ? storedLanguage : 'ru';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((nextLanguage: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) => {
      const template = translations[language][key];
      if (!params) return template;

      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        Object.hasOwn(params, name) ? String(params[name]) : match,
      );
    },
    [language],
  );

  const et = useCallback(
    (source: string, params?: TranslationParams) => {
      const template = language === 'en' ? (editorTranslationsEn[source] ?? source) : source;
      if (!params) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        Object.hasOwn(params, name) ? String(params[name]) : match,
      );
    },
    [language],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ language, locale: language === 'ru' ? 'ru-RU' : 'en-US', setLanguage, t, et }),
    [language, setLanguage, t, et],
  );

  return <I18nContext value={value}>{children}</I18nContext>;
}
